import { queryOptions } from '@tanstack/react-query';
import * as allergensApi from '../endpoints/allergen';
import { ListAllergensParams } from '../endpoints/allergen';
import { Allergen } from '@/types/allergen';
import { Paginated } from '@/types/pagination';

/**
 * Query keys for allergens.
 */
export const ALLERGENS_QUERY_KEYS = {
  all: ['allergens'] as const,
  lists: () => [...ALLERGENS_QUERY_KEYS.all, 'list'] as const,
  list: (params: ListAllergensParams) => [...ALLERGENS_QUERY_KEYS.lists(), params] as const,
  details: () => [...ALLERGENS_QUERY_KEYS.all, 'detail'] as const,
  detail: (slug: string) => [...ALLERGENS_QUERY_KEYS.details(), slug] as const,
} as const;

/**
 * Query options for listing allergens.
 *
 * Reference data changes rarely, so it is cached with a long staleTime and
 * persisted to disk so consumers paint instantly on cold boot.
 */
export const allergenListQueryOptions = (params: ListAllergensParams = {}) =>
  queryOptions({
    queryKey: ALLERGENS_QUERY_KEYS.list(params),
    queryFn: async (): Promise<Paginated<Allergen>> => {
      return allergensApi.listAllergens(params);
    },
    staleTime: 1000 * 60 * 15, // 15 minutes (near-static reference data)
    gcTime: 1000 * 60 * 30, // 30 minutes
    meta: {
      // Near-static reference data; persist so it paints instantly.
      persist: true,
    },
  });

/**
 * Query options for fetching a single allergen by slug.
 */
export const allergenDetailQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ALLERGENS_QUERY_KEYS.detail(slug),
    queryFn: async (): Promise<Allergen> => {
      return allergensApi.getAllergen(slug);
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 15, // 15 minutes (near-static reference data)
    gcTime: 1000 * 60 * 30, // 30 minutes
    meta: {
      persist: true,
    },
  });
